import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { featureToggleSelectors } from '@makemydeal/dr-shared-store';
import * as employeeDetailsActionCreators from '@makemydeal/dr-dash-store/actions/employeeDetailsActionCreators';
import * as employeeDetailsSelectors from '@makemydeal/dr-dash-store/selectors/employeeDetailsSelectors';
import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import type { AutocompleteOptions } from '@makemydeal/dr-shared-types';
import type { EmployeeDetails, EmployeeDetailsSupportedRoles, EmployeeSearchResult, EmployeeTeam } from '@makemydeal/dr-dash-types';
import { mapRoleForSearch, roleFieldKeys } from './employeeRoleUtils';

export type InvalidFields = {
    employeeDetails?: EmployeeTeam;
    fields: Record<string, boolean>;
    roles: Set<string>;
};

export const invalidField = false;
export const populatedField = Symbol();
export const emptyField = Symbol();
const whitespace = /\s/;

export const validators = {
    firstName: (value: string) => Boolean(value.trim()),
    lastName: (value: string) => Boolean(value.trim()),
    employeeDmsId: (value: string | undefined, allowAnyContent = false) => {
        const trimmedDmsId = value?.trim();
        if (!trimmedDmsId) return emptyField;
        if (!allowAnyContent && (whitespace.test(trimmedDmsId) || trimmedDmsId.length > 50)) return invalidField;
        return populatedField;
    }
} as const;

type SearchFieldType = 'firstName' | 'lastName';
type EditableField = 'firstName' | 'lastName' | 'employeeDmsId';

export type UseEmployeeRoleHandlersProps = {
    employeeDetails: EmployeeTeam;
    setEmployeeDetails: Dispatch<SetStateAction<EmployeeTeam>>;
    invalidFields: InvalidFields;
    setInvalidFields: Dispatch<SetStateAction<InvalidFields>>;
};

export type UseEmployeeRoleHandlersReturn = {
    /** Autocomplete options per `${role}-${fieldType}` search key */
    employeeSearchResults: Record<string, AutocompleteOptions[]>;
    /** In-flight search flags per `${role}-${fieldType}` search key */
    loadingFields: Record<string, boolean>;
    /** The search key of the field whose request is currently outstanding */
    currentSearchKey: string | null;
    /** Per-role remount counter, incremented on Clear to reset the Autocomplete inputs */
    clearCounters: Record<string, number>;
    getOrInitializeEmployee: (role: EmployeeDetailsSupportedRoles) => EmployeeDetails;
    setValidFieldAfterBlur: (invalidFieldKey: string, isValid: boolean) => void;
    handleEmployeeSearch: (role: string, searchTerm: string, fieldType: SearchFieldType) => void;
    createSearchHandler: (role: string, fieldType: SearchFieldType) => (searchTerm: string) => void;
    createChangeHandler: (role: string, fieldType: SearchFieldType) => (value: string) => void;
    handleEmployeeSelect: (role: EmployeeDetailsSupportedRoles, selectedOption: AutocompleteOptions) => void;
    handleFieldChange: (role: EmployeeDetailsSupportedRoles, field: EditableField, value: string) => void;
    handleFieldBlur: (role: EmployeeDetailsSupportedRoles, field: EditableField, value: string) => void;
    handleClear: (role: EmployeeDetailsSupportedRoles) => void;
};

export type RoleFieldHandlers = Pick<
    UseEmployeeRoleHandlersReturn,
    | 'employeeSearchResults'
    | 'loadingFields'
    | 'currentSearchKey'
    | 'clearCounters'
    | 'createSearchHandler'
    | 'createChangeHandler'
    | 'handleEmployeeSelect'
    | 'handleFieldChange'
    | 'handleFieldBlur'
    | 'handleClear'
>;

export const useEmployeeRoleHandlers = (props: UseEmployeeRoleHandlersProps): UseEmployeeRoleHandlersReturn => {
    const { employeeDetails, setEmployeeDetails, invalidFields, setInvalidFields } = props;
    const dispatch = useDispatch();

    const commonOrgId = useSelector(offerReduxSelectors.getCommonOrgId);
    const searchResults = useSelector(employeeDetailsSelectors.getSearchResults);
    const searchLoading = useSelector(employeeDetailsSelectors.getSearchLoading);
    const isAdditionalSalesRolesEnabled = useSelector(featureToggleSelectors.isAdditionalSalesRolesEnabled);

    const [employeeSearchResults, setEmployeeSearchResults] = useState<Record<string, AutocompleteOptions[]>>({});
    const [loadingFields, setLoadingFields] = useState<Record<string, boolean>>({});
    const [currentSearchKey, setCurrentSearchKey] = useState<string | null>(null);
    const [clearCounters, setClearCounters] = useState<Record<string, number>>({});

    const getOrInitializeEmployee = (role: EmployeeDetailsSupportedRoles): EmployeeDetails => {
        return (
            employeeDetails[role] || {
                employeeDmsId: undefined,
                employeeRole: role,
                name: { firstName: '', lastName: '' }
            }
        );
    };

    const setValidFieldAfterBlur = (invalidFieldKey: string, isValid: boolean) => {
        if (!isValid) {
            return;
        }

        // istanbul ignore next
        const { [invalidFieldKey]: isCurrentlyInvalid, ...fields } = invalidFields.fields;
        if (isCurrentlyInvalid) {
            setInvalidFields({ ...invalidFields, fields });
        }
    };

    const handleEmployeeSearch = useCallback(
        (role: string, searchTerm: string, fieldType: SearchFieldType) => {
            const searchKey = `${role}-${fieldType}`;

            if (!searchTerm || searchTerm.length < 3) {
                setEmployeeSearchResults((prev) => ({ ...prev, [searchKey]: [] }));

                setLoadingFields((prev) => {
                    const updated = { ...prev };
                    delete updated[searchKey];
                    return updated;
                });

                setCurrentSearchKey((prevKey) => {
                    if (prevKey === searchKey) return null;
                    return prevKey;
                });

                return;
            }

            setCurrentSearchKey(searchKey);

            setLoadingFields((prev) => ({
                ...prev,
                [searchKey]: true
            }));

            setEmployeeSearchResults((prev) => ({ ...prev, [searchKey]: [] }));

            const apiRole = mapRoleForSearch(role);
            const searchParams = {
                commonOrgId: String(commonOrgId),
                role: apiRole,
                ...(fieldType === 'firstName' ? { firstName: searchTerm } : { lastName: searchTerm })
            };

            dispatch(employeeDetailsActionCreators.searchEmployees(searchParams));
        },
        [dispatch, commonOrgId]
    );

    const createSearchHandler = useCallback(
        (role: string, fieldType: SearchFieldType) => (searchTerm: string) => {
            handleEmployeeSearch(role, searchTerm, fieldType);
        },
        [handleEmployeeSearch]
    );

    const createChangeHandler = useCallback(
        (role: string, fieldType: SearchFieldType) => (value: string) => {
            setEmployeeDetails((prev) => {
                const currentEmployee = prev[role as EmployeeDetailsSupportedRoles];
                const employee = currentEmployee
                    ? {
                          ...currentEmployee,
                          name: {
                              ...currentEmployee.name,
                              [fieldType]: value
                          }
                      }
                    : {
                          employeeDmsId: undefined,
                          employeeRole: role as EmployeeDetailsSupportedRoles,
                          name: {
                              firstName: fieldType === 'firstName' ? value : '',
                              lastName: fieldType === 'lastName' ? value : ''
                          }
                      };
                return {
                    ...prev,
                    [role as EmployeeDetailsSupportedRoles]: employee
                };
            });
        },
        []
    );

    useEffect(() => {
        if (!searchLoading && currentSearchKey) {
            setLoadingFields((prev) => {
                const updated = { ...prev };
                delete updated[currentSearchKey];
                return updated;
            });

            if (searchResults && searchResults.length > 0) {
                const options: AutocompleteOptions[] = searchResults.map((employee: EmployeeSearchResult) => ({
                    label: `${employee.firstName} ${employee.lastName}`,
                    value: employee.employeeNumber
                }));

                setEmployeeSearchResults((prev) => ({
                    ...prev,
                    [currentSearchKey]: options
                }));
            }
        }
    }, [searchLoading, searchResults, currentSearchKey]);

    const handleEmployeeSelect = useCallback(
        (role: EmployeeDetailsSupportedRoles, selectedOption: AutocompleteOptions) => {
            const [firstName, ...lastNameParts] = selectedOption.label.split(' ');
            const lastName = lastNameParts.join(' ');

            const employee: EmployeeDetails = {
                employeeDmsId: selectedOption.value,
                employeeRole: role,
                name: { firstName, lastName }
            };

            setEmployeeDetails((prev) => ({ ...prev, [role]: employee }));

            const ownKeys = roleFieldKeys(role);

            setEmployeeSearchResults((prev) => {
                const updated = { ...prev };
                Object.keys(prev).forEach((searchKey) => {
                    if (ownKeys.includes(searchKey)) {
                        updated[searchKey] = [];
                    }
                });
                return updated;
            });

            setLoadingFields((prev) => {
                const updated = { ...prev };
                Object.keys(prev).forEach((key) => {
                    if (ownKeys.includes(key)) {
                        delete updated[key];
                    }
                });
                return updated;
            });

            if (currentSearchKey && ownKeys.includes(currentSearchKey)) {
                setCurrentSearchKey(null);
            }

            setInvalidFields((prev) => {
                const updatedRoles = new Set(prev.roles);
                updatedRoles.delete(role);

                return {
                    ...prev,
                    fields: Object.fromEntries(Object.entries(prev.fields).filter(([key]) => !ownKeys.includes(key))),
                    roles: updatedRoles
                };
            });
        },
        [currentSearchKey]
    );

    const handleFieldChange = (role: EmployeeDetailsSupportedRoles, field: EditableField, value: string) => {
        const employee = getOrInitializeEmployee(role);
        if (field === 'employeeDmsId') {
            employee.employeeDmsId = value;
        } else {
            employee.name[field] = value;
        }
        setEmployeeDetails({
            ...employeeDetails,
            [role]: employee
        });
    };

    const handleFieldBlur = (role: EmployeeDetailsSupportedRoles, field: EditableField, value: string) => {
        const key = `${role}-${field}`;
        const isValid =
            field === 'employeeDmsId'
                ? Boolean(validators.employeeDmsId(value, isAdditionalSalesRolesEnabled))
                : validators[field](value);
        setValidFieldAfterBlur(key, isValid);
    };

    const handleClear = (role: EmployeeDetailsSupportedRoles) => {
        const ownKeys = roleFieldKeys(role);

        setEmployeeDetails((prev) => ({ ...prev, [role]: undefined }));

        setEmployeeSearchResults((prev) => {
            const updated = { ...prev };
            delete updated[`${role}-firstName`];
            delete updated[`${role}-lastName`];
            return updated;
        });

        if (currentSearchKey && ownKeys.includes(currentSearchKey)) {
            setCurrentSearchKey(null);
        }

        setLoadingFields((prev) => {
            const updated = { ...prev };
            delete updated[`${role}-firstName`];
            delete updated[`${role}-lastName`];
            return updated;
        });

        setInvalidFields((prev) => ({
            fields: Object.fromEntries(Object.entries(prev.fields).filter(([key]) => !ownKeys.includes(key))),
            roles: new Set(Array.from(prev.roles).filter((r) => r !== role))
        }));

        setClearCounters((prev) => ({
            ...prev,
            [role]: (prev[role] || 0) + 1
        }));
    };

    return {
        employeeSearchResults,
        loadingFields,
        currentSearchKey,
        clearCounters,
        getOrInitializeEmployee,
        setValidFieldAfterBlur,
        handleEmployeeSearch,
        createSearchHandler,
        createChangeHandler,
        handleEmployeeSelect,
        handleFieldChange,
        handleFieldBlur,
        handleClear
    };
};
